import React from 'react';
import PropTypes from 'prop-types';
import Header from '../common/Header';

/**
 * MainPageLayout component
 *
 * Provides a structured layout with a fixed header, an optional page title (and icon), and a flexible content section.
 * It ensures consistency across pages by maintaining spacing, typography, and responsive design.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} [props.children] - The main content of the page.
 * @param {string} [props.pageTitle] - The title displayed at the top of the page.
 * @param {React.ReactNode} [props.icon] - An optional icon or component to display next to the title.
 * @param {React.ReactNode} [props.headerControls] - Optional controls (buttons, etc.) to render alongside the title.
 * @param {Object} [props.customHeaderProps] - Additional props passed to the Header component.
 *
 * @returns {JSX.Element} The rendered layout structure.
 */
const MainPageLayout = ({ children, pageTitle, icon, headerControls, customHeaderProps }) => {
  return (
    <div className="bg-custom-light-gray text-black w-full min-h-screen flex flex-col">
      <Header {...customHeaderProps} />

      {/* Main content with optional title, icon & header controls */}
      <main
        className="pt-32 sm:pt-36 px-4 md:px-12 lg:px-20
         flex-1 flex flex-col pb-8"
      >
        {pageTitle && (
          <div className="flex items-center justify-between pt-[23px] pb-4 mb-2">
            <div className="flex items-center space-x-6">
              {icon}
              <h1 className="text-lg md:text-2xl lg:text-3xl font-semibold">{pageTitle}</h1>
            </div>
            {headerControls}
          </div>
        )}

        {/* Section for the main content */}
        <div className="flex-1 flex flex-col">{children}</div>
      </main>
    </div>
  );
};

MainPageLayout.propTypes = {
  children: PropTypes.node,
  pageTitle: PropTypes.string,
  icon: PropTypes.node,
  headerControls: PropTypes.node,
  customHeaderProps: PropTypes.object,
};

export default MainPageLayout;
